import {
  createItem,
  readFieldsByCollection,
  readItem,
  updateItem,
} from "@directus/sdk";
import {
  generateZodSchema,
  mapDirectusFieldsToFormFields,
} from "../utils/field-mapping.js";
import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

export function useDirectusForm({
  client,
  collection,
  itemId = null, // For editing existing items
  defaultValues = {},
  onSuccess,
  onError,
  excludeFields = [],
  includeFields = [],
}) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [schema, setSchema] = useState(null);

  // Initialize react-hook-form
  const form = useForm({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
  });

  // Fetch collection fields and item data
  useEffect(() => {
    const fetchFormData = async () => {
      if (!client || !collection) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch collection fields
        const fieldsResponse = await client.request(
          readFieldsByCollection(collection)
        );

        // Filter out system fields and map to form fields
        let formFields = mapDirectusFieldsToFormFields(fieldsResponse);

        if (includeFields.length > 0) {
          formFields = formFields.filter((f) => includeFields.includes(f.name));
        } else if (excludeFields.length > 0) {
          formFields = formFields.filter(
            (f) => !excludeFields.includes(f.name)
          );
        }

        setFields(formFields);

        // Generate Zod schema from fields
        const zodSchema = generateZodSchema(formFields);
        setSchema(zodSchema);

        // If editing, fetch existing item data
        if (itemId) {
          const itemData = await client.request(readItem(collection, itemId));
          form.reset({ ...defaultValues, ...itemData });
        } else {
          form.reset(defaultValues);
        }
      } catch (err) {
        setError(err);
        console.error("DirectusForm fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFormData();
  }, [client, collection, itemId, JSON.stringify(defaultValues)]);

  // Submit handler
  const handleSubmit = async (data) => {
    if (!client || !collection) return;

    setSubmitting(true);
    setError(null);

    try {
      let result;

      if (itemId) {
        // Update existing item
        result = await client.request(updateItem(collection, itemId, data));
      } else {
        // Create new item
        result = await client.request(createItem(collection, data));
      }

      onSuccess?.(result, data);

      // Reset form if creating new item
      if (!itemId) {
        form.reset(defaultValues);
      }
    } catch (err) {
      setError(err);
      onError?.(err, data);
      console.error("DirectusForm submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    form,
    fields,
    loading,
    submitting,
    error,
    // handleSubmit: () => {
    //   return new Promise((resolve, reject) => {
    //     form.handleSubmit(
    //       (fields) => {
    //         resolve(handleSubmit(fields));
    //       },
    //       (errors) => {
    //         reject(errors);
    //         console.error(errors);
    //         throw errors;
    //       }
    //     )();
    //   });
    // },
    handleSubmit: form.handleSubmit(handleSubmit),
    isEditing: !!itemId,
  };
}
